// src/pages/fidele/ViewFidele.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Row, Badge, Spinner, Alert, Modal, Form, Button, ProgressBar, Nav } from "react-bootstrap";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { COLORS } from "../../constants/colors";
import { Colors } from "chart.js";

export default function ViewFidele() {
  const navigate = useNavigate();
  const { matricule } = useParams();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [stats, setStats] = useState(null);
  const [activites, setActivites] = useState([]);
  const [loadingActivites, setLoadingActivites] = useState(false);

  // États pour 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFADetails, setTwoFADetails] = useState(null);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAPassword, setTwoFAPassword] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);

  // États pour les modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editForm, setEditForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    adresse: "",
    profession: ""
  });

  const [passwordForm, setPasswordForm] = useState({
    ancien: "",
    nouveau: "",
    confirmation: ""
  });

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    fetchProfile();
  }, [matricule]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/login');
        return;
      }

      const currentUser = JSON.parse(userStr);
      setUser(currentUser);

      const userId = currentUser.id || currentUser.id_utilisateur;
      const mat = matricule || currentUser.matricule;

      const response = await fetch(`https://josephsbusiness.com/api/membres.php?matricule=${mat}&user_id=${userId}`);

      if (!response.ok) throw new Error('Erreur chargement profil');

      const result = await response.json();

      if (result.success) {
        setProfile(result.data || {});
        setStats(result.stats || {});

        if (result.data?.roles && typeof result.data.roles === 'string') {
          try {
            result.data.roles = JSON.parse(result.data.roles);
          } catch (e) {
            result.data.roles = [];
          }
        }

        if (result.data) {
          setEditForm({
            nom: result.data.nom || "",
            prenom: result.data.prenom || "",
            email: result.data.email || "",
            telephone: result.data.contact || result.data.telephone || "",
            adresse: result.data.adresse || "",
            profession: result.data.form_seculiere || result.data.profession || ""
          });
        }

        await fetch2FAStatus(userId, result.data?.id);

        if (result.data?.id) {
          await fetchActivites(userId, result.data.id);
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetch2FAStatus = async (userId, membreId) => {
    try {
      const response = await fetch(
        `https://josephsbusiness.com/api/2fa.php?user_id=${userId}&membre_id=${membreId}`
      );
      const result = await response.json();
      if (result.success) {
        setTwoFAEnabled(result.enabled);
        setTwoFADetails(result.details || null);
      }
    } catch (err) {
      console.error('Erreur chargement 2FA:', err);
    }
  };

  const fetchActivites = async (userId, membreId) => {
    try {
      setLoadingActivites(true);
      const response = await fetch(
        `https://josephsbusiness.com/api/activites.php?user_id=${userId}&membre_id=${membreId}&limite=200`
      );
      const result = await response.json();
      if (result.success) {
        setActivites(result.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement activités:', err);
    } finally {
      setLoadingActivites(false);
    }
  };

  const handleUpdateProfile = async () => {
    setSaving(true);
    setFormError("");
    try {
      const userStr = localStorage.getItem('user');
      const currentUser = JSON.parse(userStr);
      const userId = currentUser.id || currentUser.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/membres.php?id=${profile.id}&user_id=${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({
          nom: editForm.nom,
          prenom: editForm.prenom,
          email: editForm.email,
          contact: editForm.telephone,
          adresse: editForm.adresse,
          form_seculiere: editForm.profession
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Profil mis à jour avec succès');
        setShowEditModal(false);
        await fetchProfile();
      } else {
        setFormError(result.message);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setFormError(err.message);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.nouveau !== passwordForm.confirmation) {
      setFormError("Les mots de passe ne correspondent pas");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const userStr = localStorage.getItem('user');
      const currentUser = JSON.parse(userStr);
      const userId = currentUser.id || currentUser.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/change_password.php?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          matricule: profile.matricule,
          ancien: passwordForm.ancien,
          nouveau: passwordForm.nouveau
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Mot de passe modifié avec succès');
        setShowPasswordModal(false);
        setPasswordForm({ ancien: "", nouveau: "", confirmation: "" });
      } else {
        setFormError(result.message);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setFormError(err.message);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle2FA = async () => {
    if (!twoFAPassword) {
      setFormError("Veuillez entrer votre mot de passe");
      return;
    }

    setTwoFALoading(true);
    setFormError("");
    try {
      const userStr = localStorage.getItem('user');
      const currentUser = JSON.parse(userStr);
      const userId = currentUser.id || currentUser.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/2fa.php?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membre_id: profile.id,
          enabled: !twoFAEnabled,
          password: twoFAPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(twoFAEnabled ? '2FA désactivée avec succès' : '2FA activée avec succès');
        setShow2FAModal(false);
        setTwoFAPassword("");
        await fetch2FAStatus(userId, profile.id);
      } else {
        setFormError(result.message);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setFormError(err.message);
      toast.error(err.message);
    } finally {
      setTwoFALoading(false);
    }
  };

  const getInitials = () => {
    if (!profile) return 'U';
    const prenom = profile.prenom || '';
    const nom = profile.nom || '';
    return (prenom.charAt(0) + nom.charAt(0)).toUpperCase() || 'U';
  };

  const getFullName = () => {
    if (!profile) return 'Utilisateur';
    const prenom = profile.prenom || '';
    const nom = profile.nom || '';
    return `${prenom} ${nom}`.trim() || 'Utilisateur';
  };

  const getRoleBadge = (role) => {
    const colors = {
      'Super Administrateur': COLORS.danger,
      'Administrateur': COLORS.primary,
      'Pasteur': COLORS.purple,
      'Ancien': COLORS.info,
      'Diacre': COLORS.success,
      'Diaconesse': COLORS.success,
      'DP': COLORS.warning,
      'Accueil': COLORS.secondary,
      'Nouveau': COLORS.gray
    };

    return (
      <span className="badge" style={{
        background: colors[role] || COLORS.secondary,
        padding: '6px 14px',
        borderRadius: '30px',
        fontWeight: '500',
        fontSize: '0.8rem'
      }}>
        {role || 'Membre'}
      </span>
    );
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);

      if (diffSec < 60) return "À l'instant";
      if (diffMin < 60) return `Il y a ${diffMin} min`;
      if (diffHour < 24) return `Il y a ${diffHour}h`;
      if (diffDay < 7) return `Il y a ${diffDay}j`;
      return format(date, 'dd MMM yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement du profil</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchProfile}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const isOwnProfile = !matricule || matricule === user?.matricule;

  return (
    <>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})` }}>
                <i className="ri-user-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Profil</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-user-line"></i> {getFullName()}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}20`, color: COLORS.success }}>
                    <i className="ri-shield-line"></i> {twoFAEnabled ? '2FA activé' : '2FA désactivé'}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {isOwnProfile && (
                <button
                  className="btn-modern-outline"
                  onClick={() => navigate(`/profil/modifier/${profile.id}`)}
                >
                  <i className="ri-edit-line"></i> Modifier
                </button>
              )}
              <button className="btn-modern-outline" onClick={() => navigate(-1)}>
                <i className="ri-arrow-left-line"></i> Retour
              </button>
            </div>
          </div>

          {/* ===== CARTE DE PROFIL ===== */}
          {profile && (
            <Card className="profile-card-modern mb-4 fade-in" style={{
              border: 'none',
              borderRadius: '0px',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
              overflow: 'hidden'
            }}>
             
              <Card.Body className="pt-0 px-4 pb-4">
                <div className="d-flex flex-column flex-md-row align-items-center gap-4" style={{ marginTop: '20px' }}>
                  <div className="profile-avatar-large" style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '40px',
                    fontWeight: 'bold',
                    border: '4px solid white',
                    boxShadow: '0 10px 30px rgba(41,90,167,0.25)',
                    flexShrink: 0
                  }}>
                    {getInitials()}
                  </div>
                  <div className="flex-grow-1 text-center text-md-start">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <h2 className="fw-bold mb-0" style={{ color: COLORS.primary, fontSize: '1.8rem' }}>
                        {getFullName()}
                      </h2>
                      {getRoleBadge(profile.role)}
                    </div>
                    <div className="d-flex flex-wrap gap-3 small">
                      <div>
                        <i className="ri-mail-line me-2" style={{ color: COLORS.primary }}></i>
                        <span>{profile.email || 'Non renseigné'}</span>
                      </div>
                      <div>
                        <i className="ri-phone-line me-2" style={{ color: COLORS.primary }}></i>
                        <span>{profile.contact || profile.telephone || 'Non renseigné'}</span>
                      </div>
                      <div>
                        <i className="ri-calendar-line me-2" style={{ color: COLORS.primary }}></i>
                        <span>Membre depuis {formatDate(profile.date_creation)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ===== STATISTIQUES ===== */}
          {stats && (
            <Row className="g-3 mb-4 fade-in">
              <Col sm={6} lg={4}>
                <div className="kpi-card kpi-primary">
                  <div className="kpi-icon"><i className="ri-history-line fs-24"></i></div>
                  <div className="kpi-content">
                    <span className="kpi-label">Activités</span>
                    <h3 className="kpi-value">{stats.activites || 0}</h3>
                  </div>
                </div>
              </Col>
              <Col sm={6} lg={4}>
                <div className="kpi-card" style={{ borderLeftColor: COLORS.success }}>
                  <div className="kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: COLORS.success }}>
                    <i className="ri-star-line fs-24"></i>
                  </div>
                  <div className="kpi-content">
                    <span className="kpi-label">Années</span>
                    <h3 className="kpi-value" style={{ color: COLORS.success }}>{stats.annees || 0}</h3>
                  </div>
                </div>
              </Col>
              <Col sm={6} lg={4}>
                <div className="kpi-card" style={{ borderLeftColor: twoFAEnabled ? COLORS.success : COLORS.gray }}>
                  <div className="kpi-icon" style={{ background: twoFAEnabled ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)', color: twoFAEnabled ? COLORS.success : COLORS.gray }}>
                    <i className="ri-shield-line fs-24"></i>
                  </div>
                  <div className="kpi-content">
                    <span className="kpi-label">2FA</span>
                    <h3 className="kpi-value" style={{ color: twoFAEnabled ? COLORS.success : COLORS.gray, fontSize: '1.2rem' }}>
                      <span className="badge rounded-pill" style={{
                        background: twoFAEnabled ? COLORS.success : COLORS.gray,
                        color: 'white',
                        padding: '0.25rem 0.8rem',
                        fontSize: '0.75rem'
                      }}>
                        {twoFAEnabled ? 'Activé' : 'Désactivé'}
                      </span>
                    </h3>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* ===== NAVIGATION PAR ONGLETS ===== */}
          <Nav variant="pills" className="nav-pills-custom mb-4 flex-wrap fade-in" style={{
            background: '#FDB52F',
            backdropFilter: 'blur(8px)',
            borderRadius: '0px',
            padding: '0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
          }}>
            <Nav.Link
              className={activeTab === 'info' ? 'active' : ''}
              onClick={() => setActiveTab('info')}
              style={{
                borderRadius: '4px',
                padding: '0.5rem 1.5rem',
                fontWeight: '500',
                color: activeTab === 'info' ? 'white' : '#64748b',
                background: activeTab === 'info' ? COLORS.primary : 'transparent',
                border: 'none'
              }}
            >
              <i className="ri-information-line me-2"></i>
              Informations
            </Nav.Link>
            <Nav.Link
              className={activeTab === 'activite' ? 'active' : ''}
              onClick={() => setActiveTab('activite')}
              style={{
                borderRadius: '4px',
                padding: '0.5rem 1.5rem',
                fontWeight: '500',
                color: activeTab === 'activite' ? 'white' : '#64748b',
                background: activeTab === 'activite' ? COLORS.primary : 'transparent',
                border: 'none'
              }}
            >
              <i className="ri-history-line me-2"></i>
              Activité
            </Nav.Link>
            {isOwnProfile && (
              <Nav.Link
                className={activeTab === 'securite' ? 'active' : ''}
                onClick={() => setActiveTab('securite')}
                style={{
                  borderRadius: '4px',
                  padding: '0.5rem 1.5rem',
                  fontWeight: '500',
                  color: activeTab === 'securite' ? 'white' : '#64748b',
                  background: activeTab === 'securite' ? COLORS.primary : 'transparent',
                  border: 'none'
                }}
              >
                <i className="ri-lock-line me-2"></i>
                Sécurité
              </Nav.Link>
            )}
          </Nav>

          {/* ===== CONTENU DES ONGLETS ===== */}
          <div className="fade-in">
            {activeTab === 'info' && profile && (
              <Row className="g-3">
                <Col md={6}>
                  <Card className="info-card-modern" style={{
                    border: 'none',
                    borderRadius: '0px',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    height: '100%'
                  }}>
                    <Card.Body>
                      <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                        <i className="ri-user-line me-2" style={{ color: COLORS.primary }}></i>
                        Informations personnelles
                      </h6>
                      <div className="info-item mb-3">
                        <span className="info-label">Nom complet</span>
                        <div className="info-value">{profile.prenom || ''} {profile.nom || ''}</div>
                      </div>
                      <div className="info-item mb-3">
                        <span className="info-label">Date de naissance</span>
                        <div className="info-value">{formatDate(profile.datenaiss)}</div>
                      </div>
                      <div className="info-item mb-3">
                        <span className="info-label">Lieu de naissance</span>
                        <div className="info-value">{profile.lieunaiss || '-'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <span className="info-label">Sexe</span>
                        <div className="info-value">
                          {profile.gendre === 'Homme' ? 'Masculin' : profile.gendre === 'Femme' ? 'Féminin' : '-'}
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Situation</span>
                        <div className="info-value">{profile.situtaion_maritale || '-'}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="info-card-modern" style={{
                    border: 'none',
                    borderRadius: '0px',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    height: '100%'
                  }}>
                    <Card.Body>
                      <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                        <i className="ri-contacts-line me-2" style={{ color: COLORS.primary }}></i>
                        Contact
                      </h6>
                      <div className="info-item mb-3">
                        <span className="info-label">Email</span>
                        <div className="info-value">{profile.email || '-'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <span className="info-label">Téléphone</span>
                        <div className="info-value">{profile.contact || profile.telephone || '-'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <span className="info-label">Profession</span>
                        <div className="info-value">{profile.form_seculiere || profile.profession || '-'}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="info-card-modern" style={{
                    border: 'none',
                    borderRadius: '0px',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                  }}>
                    <Card.Body>
                      <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                        <i className="ri-church-line me-2" style={{ color: COLORS.primary }}></i>
                        Informations religieuses
                      </h6>
                      <Row>
                        <Col md={4}>
                          <div className="info-item mb-2">
                            <span className="info-label">Église</span>
                            <div className="info-value">{profile.nom_eglise || 'JOSEPHSBUSINESS'}</div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="info-item mb-2">
                            <span className="info-label">Rôle</span>
                            <div className="info-value">{profile.role || '-'}</div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="info-item mb-2">
                            <span className="info-label">Matricule</span>
                            <div className="info-value">{profile.matricule || '-'}</div>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col md={6}>
                          <div className="info-item">
                            <span className="info-label">Date baptême eau</span>
                            <div className="info-value">{formatDate(profile.date_bapt_eau)}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="info-item">
                            <span className="info-label">Date baptême saint</span>
                            <div className="info-value">{formatDate(profile.date_bapt_saint)}</div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {activeTab === 'activite' && (
              <Card className="info-card-modern" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
              }}>
                <Card.Body>
                  <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-history-line me-2" style={{ color: COLORS.primary }}></i>
                    Historique des activités
                  </h6>
                  {loadingActivites ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" style={{ color: COLORS.primary }} />
                      <p className="mt-2 text-muted small">Chargement...</p>
                    </div>
                  ) : activites.length > 0 ? (
                    <div className="timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {activites.map((item, index) => (
                        <div key={index} className="timeline-item" style={{
                          position: 'relative',
                          paddingLeft: '30px',
                          paddingBottom: '20px',
                          borderLeft: `2px solid ${COLORS.primary}30`
                        }}>
                          <div style={{
                            position: 'absolute',
                            left: '-8px',
                            top: '4px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: COLORS.primary,
                            border: `2px solid white`,
                            boxShadow: '0 2px 8px rgba(41,90,167,0.2)'
                          }} />
                          <div className="d-flex gap-3">
                            <div className="activity-icon" style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              background: `${COLORS.primary}20`,
                              color: COLORS.primary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <i className="ri-file-list-line"></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <p className="fw-medium mb-1" style={{ fontSize: '0.9rem' }}>{item.description || 'Activité'}</p>
                                  <small className="text-muted">{item.details || ''}</small>
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>{getRelativeTime(item.date)}</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="ri-inbox-line fs-2 text-muted mb-2 d-block" style={{ opacity: 0.3 }}></i>
                      <p className="text-muted small">Aucune activité récente</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {activeTab === 'securite' && isOwnProfile && profile && (
              <Row className="g-3">
                <Col md={6}>
                  <Card className="info-card-modern" style={{
                    border: 'none',
                    borderRadius: '0px',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                  }}>
                    <Card.Body>
                      <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                        <i className="ri-lock-line me-2" style={{ color: COLORS.primary }}></i>
                        Mot de passe
                      </h6>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Dernière modification</p>
                          <p className="fw-medium mb-0">{profile.dernier_changement_mdp ? formatDate(profile.dernier_changement_mdp) : 'Jamais'}</p>
                        </div>
                        <button
                          className="btn-modern-outline"
                          onClick={() => setShowPasswordModal(true)}
                          style={{ fontSize: '0.8rem', padding: '0.3rem 1.2rem' }}
                        >
                          <i className="ri-refresh-line me-1"></i> Changer
                        </button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="info-card-modern" style={{
                    border: 'none',
                    borderRadius: '0px',
                    background: 'rgba(255,255,255,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                  }}>
                    <Card.Body>
                      <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                        <i className="ri-shield-line me-2" style={{ color: COLORS.primary }}></i>
                        Authentification à deux facteurs
                      </h6>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="badge rounded-pill" style={{
                            background: twoFAEnabled ? COLORS.success : COLORS.gray,
                            color: 'white',
                            padding: '0.25rem 0.8rem',
                            fontSize: '0.7rem'
                          }}>
                            {twoFAEnabled ? 'Activé' : 'Désactivé'}
                          </span>
                          {twoFADetails && (
                            <small className="d-block text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                              Activé le {formatDate(twoFADetails.created_at)}
                            </small>
                          )}
                        </div>
                        <button
                          className="btn-modern-outline"
                          onClick={() => setShow2FAModal(true)}
                          style={{ fontSize: '0.8rem', padding: '0.3rem 1.2rem' }}
                        >
                          {twoFAEnabled ? 'Désactiver' : 'Activer'}
                        </button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </div>

          <Footer />
        </div>
      </div>

      {/* ===== MODALE DE MODIFICATION ===== */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" className="delete-modal-modern">
        <Modal.Header closeButton className="border-0 pb-0" style={{
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px'
        }}>
          <Modal.Title className="w-100 text-center">
            <h5 className="fw-bold text-white mb-0">
              <i className="ri-edit-line me-2"></i>Modifier le profil
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4">
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Nom</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.nom}
                    onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Prénom</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.prenom}
                    onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Téléphone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.telephone}
                    onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Adresse</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.adresse}
                    onChange={(e) => setEditForm({ ...editForm, adresse: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Profession</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.profession}
                    onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })}
                    style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button variant="light" onClick={() => setShowEditModal(false)} style={{ borderRadius: '4px', padding: '0.5rem 1.5rem' }}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateProfile}
            disabled={saving}
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500'
            }}
          >
            {saving ? <Spinner size="sm" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== MODALE CHANGEMENT DE MOT DE PASSE ===== */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="delete-modal-modern">
        <Modal.Header closeButton className="border-0 pb-0" style={{
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px'
        }}>
          <Modal.Title className="w-100 text-center">
            <h5 className="fw-bold text-white mb-0">
              <i className="ri-lock-password-line me-2"></i>Changer le mot de passe
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4">
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Ancien mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.ancien}
                onChange={(e) => setPasswordForm({ ...passwordForm, ancien: e.target.value })}
                style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.nouveau}
                onChange={(e) => setPasswordForm({ ...passwordForm, nouveau: e.target.value })}
                style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Confirmer</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmation}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmation: e.target.value })}
                style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button variant="light" onClick={() => setShowPasswordModal(false)} style={{ borderRadius: '4px', padding: '0.5rem 1.5rem' }}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={saving}
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500'
            }}
          >
            {saving ? <Spinner size="sm" /> : 'Changer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== MODALE 2FA ===== */}
      <Modal show={show2FAModal} onHide={() => { setShow2FAModal(false); setTwoFAPassword(""); setFormError(""); }} centered className="delete-modal-modern">
        <Modal.Header closeButton className="border-0 pb-0" style={{
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px'
        }}>
          <Modal.Title className="w-100 text-center">
            <h5 className="fw-bold text-white mb-0">
              <i className="ri-shield-keyhole-line me-2"></i>
              {twoFAEnabled ? "Désactiver la 2FA" : "Activer la 2FA"}
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4 px-4">
          {formError && <Alert variant="danger">{formError}</Alert>}
          <div className="text-center py-2">
            <div className="display-1 mb-3" style={{ color: COLORS.primary }}>
              <i className="ri-shield-line"></i>
            </div>
            <h5 className="fw-semibold mb-2">{twoFAEnabled ? "Pour désactiver la 2FA" : "Pour activer la 2FA"}</h5>
            <p className="text-muted small mb-3">
              {twoFAEnabled
                ? "Confirmez votre mot de passe pour désactiver l'authentification à deux facteurs."
                : "L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte."}
            </p>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem', color: COLORS.primaryDark }}>Mot de passe de confirmation</Form.Label>
              <Form.Control
                type="password"
                value={twoFAPassword}
                onChange={(e) => setTwoFAPassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                style={{ borderRadius: '4px', border: '1px solid #e2e8f0' }}
              />
            </Form.Group>
            <div className="alert alert-info py-2 px-3 text-start small" style={{ borderRadius: '4px', borderLeft: `3px solid ${COLORS.info}` }}>
              <i className="ri-information-line me-1"></i>
              Vous devez confirmer votre mot de passe pour {twoFAEnabled ? "désactiver" : "activer"} la 2FA.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
          <Button variant="light" onClick={() => { setShow2FAModal(false); setTwoFAPassword(""); }} style={{ borderRadius: '4px', padding: '0.5rem 1.5rem' }}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleToggle2FA}
            disabled={twoFALoading || !twoFAPassword}
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1.5rem',
              fontWeight: '500'
            }}
          >
            {twoFALoading ? <Spinner size="sm" /> : (twoFAEnabled ? "Désactiver" : "Activer")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .profile-card-modern {
          transition: all 0.3s ease;
        }
        .profile-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .info-card-modern {
          transition: all 0.3s ease;
        }
        .info-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.06) !important;
        }

        .info-item {
          margin-bottom: 0.5rem;
        }
        .info-label {
          display: block;
          font-size: 0.7rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }
        .info-value {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
        }

        .nav-pills-custom .nav-link {
          transition: all 0.2s ease;
        }
        .nav-pills-custom .nav-link:hover:not(.active) {
          background: rgba(41,90,167,0.08);
        }

        .timeline-item:last-child {
          padding-bottom: 0;
        }

        .profile-avatar-large {
          animation: scaleIn 0.4s ease-out;
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .delete-modal-modern .modal-content {
          border: none;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
          .profile-avatar-large {
            width: 80px;
            height: 80px;
            font-size: 32px;
          }
          .kpi-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </>
  );
}